import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FormElement from "rc-field-form";
import { Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import Field from "../../../../components/Field";
import DateInput from "../../../../components/Inputs/DateInput";
import Button from "../../../../components/Button";
import ResourceSelect from "../../../../components/Inputs/ResourceSelect";
import FileUploader from "../UploadInput";
import { useImpactConfirmModal } from "../hooks/useImpactConfirmModal";
import { useQualificationsImpact } from "../hooks/useQualificationsImpact";
import { useShiftActionsResultModal } from "../hooks/useShiftActionsResultModal";
import { ProfileContext } from "../../context";
import { getSettingsResourceSearchFilters, renderSettingsResourceLabel } from "../../../../utils/helpers/settings";
import { buildQualificationsImpactList } from "../helpers";

const Form = ({ closeDrawer, selected, data, upload, remove, updateLicense, addLicense }) => {
    const { t } = useTranslation();
    const [form] = FormElement.useForm();
    const [selectedFiles, setSelectedFiles] = useState(selected?.documents);
    const [filesError, setFilesError] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const { isSelf, user } = useContext(ProfileContext);
    const { checkImpact, checkingImpact, applyShiftActions } = useQualificationsImpact();
    const { open: openResultModal, modal: resultModal } = useShiftActionsResultModal();

    const submit = useCallback(
        async (values, shiftActions) => {
            const submitUrl = isSelf ? "/profile/license" : `/users/${user?.id}/license`;
            const updateUrl = isSelf
                ? `/profile/license/${selected?.id}`
                : `/users/${user?.id}/license/${selected?.id}`;
            const uploadUrl = isSelf ? "/upload/profile/license" : `/upload/users/${user?.id}/license`
            setUploadLoading(true);

            try {
                const _selectedFiles = selectedFiles?.filter((item) => typeof item !== "string");
                let uploadedFiles = [];
                if (_selectedFiles?.length > 0) {
                    uploadedFiles = await upload(uploadUrl, _selectedFiles);
                }

                const data = {
                    ...values,
                    startDate: moment(values?.startDate).format("YYYY-MM-DD"),
                    endDate: values?.endDate ? moment(values?.endDate).format("YYYY-MM-DD") : undefined,
                    type: values?.type?.id,
                    documents: [
                        ...(selectedFiles?.filter((file) => typeof file === "string") || []),
                        ...uploadedFiles?.map((item) => item.path)
                    ],
                };
                const filesToRemove = data.documents.filter((document) => !selected?.documents?.includes(document));
                const saved = selected
                    ? await updateLicense(updateUrl, data)
                    : await addLicense(submitUrl, data);
                if (saved) {
                    // Keep the drawer open until the result modal is closed.
                    await openResultModal(await applyShiftActions(shiftActions));
                }
                if (filesToRemove?.length > 0) {
                    await remove(filesToRemove);
                }
                closeDrawer();
                setSelectedFiles([]);
            } catch (error) {
                toast.error(t("upload-failed"));
            } finally {
                setUploadLoading(false);
            }
        },
        [isSelf, user?.id, selected, selectedFiles, t, upload, closeDrawer, remove, updateLicense, addLicense, applyShiftActions, openResultModal]
    );


    const { open: openConfirm, modal: confirmModal } = useImpactConfirmModal({
        message: t(selected ? "are-you-sure-update-license" : "are-you-sure-add-license"),
        onConfirm: ({ values }, shiftActions) => {
            submit(values, shiftActions);
        },
    });

    const onFinish = useCallback(
        async (values) => {
            if (selectedFiles?.length === 0 || !selectedFiles) {
                setFilesError(t("upload-at-least-a-file"));
                return;
            }

            setFilesError(null);
            // The legacy api stores the raw response, which wraps the list in `result`.
            const list = Array.isArray(data) ? data : data?.result;
            try {
                const impact = await checkImpact({
                    licenses: buildQualificationsImpactList(list, { ...values, id: selected?.id }),
                });
                openConfirm({ values, impact });
            } catch {
                // Error toast is shown by checkImpact.
            }
        },
        [selectedFiles, t, checkImpact, data, selected?.id, openConfirm]
    );

    useEffect(() => {
        form.resetFields();
        if (selected?.id) {
            form.setFieldsValue({
                type: selected?.type,
                startDate: moment(selected?.startDate).toDate(),
                endDate: selected?.endDate ? moment(selected?.endDate).toDate() : null,
                state: selected?.state,
            })
        }
    }, [selected, form]);

    return (
        <FormElement form={form} onFinish={onFinish}>
            <Field name="type" label={t("type")}
                rules={[
                    {
                        required: true,
                        message: t("license-required")
                    }
                ]}
            >
                <ResourceSelect
                    labelPropName="description"
                    renderLabel={renderSettingsResourceLabel}
                    resourcePath="/license"
                    placeholder={t("licenses")}
                    allowClear
                    hasSearch
                    getSearchFilters={getSettingsResourceSearchFilters}
                />
            </Field>

            <Field
                name="startDate"
                label={t("start-date")}
                rules={[
                    {
                        required: true,
                        message: t("required-start-date"),
                    },
                ]}
                className="d-flex flex-column"
            >
                <DateInput placeholderText={t("start-date")} />
            </Field>

            <Field
                name="endDate"
                label={t("end-date")}
                className="d-flex flex-column"
                rules={[
                    ({ getFieldValue }) => ({
                        validator: (_, value) => {
                            const startDate = getFieldValue("startDate");
                            if (startDate && value && moment(value).isSameOrBefore(moment(startDate), "date")) {
                                return Promise.reject(new Error(t("end-date-bigger-than-start-date")));
                            }
                            return Promise.resolve();
                        },
                    }),
                ]}
            >
                <DateInput placeholderText={t("end-date")} />
            </Field>

            <Field name="state" label={t("state")}>
                <Input type="string" placeholder={t("state")} />
            </Field>

            <Label>{t("documents")} <span className="text-danger mx-1">*</span></Label>
            <FileUploader selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} />
            {filesError && (
                <div className="invalid-feedback d-block">{filesError}</div>
            )}

            <div className="d-flex justify-content-end my-4">
                <Button
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                    onClick={closeDrawer}
                    disabled={uploadLoading}
                >
                    {t("cancel")}
                </Button>

                <Button
                    className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
                    color="primary"
                    type="submit"
                    loading={uploadLoading || checkingImpact}
                    disabled={uploadLoading || checkingImpact}
                >
                    {t("save")}
                </Button>
            </div>

            {confirmModal}
            {resultModal}
        </FormElement>
    );
};

export default Form;
